import { IHistoryLog } from "@schemas/commonSchema";
import { IRisikoSection } from "@schemas/issueBoardSchema";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { formatShortDate, isDateValidFrontend } from "../../app/routes/admin/selectors";
import { DynamicValueRenderer, resolveColumnTitle, resolveDynamicMeta } from "./HistoryLogsTableUtils";

type Props = {
  logData: IHistoryLog[];
  otherAttributesConfig?: IOtherAttributeConfigApi;
  risikoConfig?: IRisikoSection;
  isLoading?: boolean;
};

const HistoryLogsTableDeprecated: React.FC<Props> = ({
  logData,
  otherAttributesConfig,
  risikoConfig,
  isLoading = false,
}) => {
  // Enrich rows w calculated display column + meta
  const rows = useMemo(
    () =>
      (logData ?? []).map((r) => {
        const dyn = resolveDynamicMeta(r.dataField, r.columnDisplayName, otherAttributesConfig, risikoConfig);
        const title = resolveColumnTitle(r.dataField, r.columnDisplayName, otherAttributesConfig, risikoConfig);
        return {
          ...r,
          _displayColumnName: title,
          _dynMeta: dyn,
        };
      }),
    [logData, otherAttributesConfig, risikoConfig],
  );

  const columns: ColDef<any>[] = useMemo(
    () => [
      {
        field: "_displayColumnName",
        headerName: "Kolonne",
        filter: "agSetColumnFilter",
        width: 180,
        comparator: (a, b) => String(a || "").localeCompare(String(b || "")),
      },
      { field: "_id", hide: true },
      {
        field: "newValue",
        headerName: "Ny verdi",
        flex: 1,
        filter: "agTextColumnFilter",
        cellRenderer: (params) => <DynamicValueRenderer value={params.value} meta={params?.data?._dynMeta} />,
      },
      {
        field: "oldValue",
        headerName: "Forrige verdi",
        flex: 1,
        filter: "agTextColumnFilter",
        cellRenderer: (params) => <DynamicValueRenderer value={params.value} meta={params?.data?._dynMeta} />,
      },
      {
        field: "editedBy",
        headerName: "Redigert av",
        filter: "agTextColumnFilter",
        width: 160,
      },
      {
        field: "updatedAt",
        headerName: "Endret dato",
        width: 132,
        sort: "desc",
        valueFormatter: (p) => (isDateValidFrontend(p.value) ? formatShortDate(p.value) : ""),
        comparator: (a, b) => {
          const da = isDateValidFrontend(a) ? new Date(a).getTime() : 0;
          const db = isDateValidFrontend(b) ? new Date(b).getTime() : 0;
          return da - db;
        },
      },
    ],
    [],
  );

  return (
    <div className="ag-theme-quartz h-100 w-100">
      <AgGridReact
        domLayout="autoHeight"
        rowData={rows}
        columnDefs={columns}
        loading={isLoading}
        defaultColDef={{
          suppressHeaderMenuButton: true,
          autoHeight: true,
          minWidth: 120,
          maxWidth: 500,
          cellStyle: {
            overflow: "hidden",
            whiteSpace: "normal",
            lineHeight: "1.5",
            wordBreak: "break-word",
          },
        }}
        rowHeight={45}
        suppressClickEdit
        animateRows={false}
        suppressContextMenu
        localeText={AG_GRID_LOCALE_NOR}
        getRowId={(p) => p.data._id}
      />
    </div>
  );
};

export default HistoryLogsTableDeprecated;
